import { useTranslation } from 'react-i18next';
import {
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { AuthClient } from 'api/base';
import clsx from 'clsx';
import style from './ProfilePage.module.css';
import { CustomerCard } from './components/CustomerCard';
import { NavigateButton } from './components/NavigateButton';
import { useLinks } from 'hooks/router';
import { SavedCardsCard } from './components/SavedCardsCard';
import { useState } from 'react';
import { useMediaQuery } from 'ui-kit/hooks';
import { FEButton } from 'ui-kit/components';
import { useUnit } from 'effector-react';
import { logOutUser } from 'domains/profile';
import { ProfileSidebar } from './components/NavItems/ProfileSidebar';
import { BonusPage, EditProfilePage, OrdersPage } from '..';
import { SavedCards } from 'pages/SavedCards';
import { SavedAddresses } from 'pages/SavedAddresses';
import { ProfileHeaderProvider } from './components/ProfileHeaderContext';
import { ProfileSection } from './components/ProfileSection';
import { SAVED_ADDRESSES_PATHNAME } from 'const';


const PROFILE_PAGES = {
  editProfile: 'edit-profile',
  addresses: 'addresses',
  cards: 'saved-cards',
  orders: 'orders',
  bonusHistory: 'bonus',
};

export const ProfilePage = () => {
  const { t } = useTranslation();

  const is1024 = useMediaQuery('(min-width: 1024px)');
  const is500 = useMediaQuery('(min-width: 500px)');

  const [withCards, setWithCards] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { ordersLink, bonusLink } = useLinks();

  const handleLogOut = useUnit(logOutUser);

  const isAuth = AuthClient.isAuth;

  const PROFILE_PAGES = {
    editProfile: 'edit-profile',
    addresses: 'addresses',
    cards: 'saved-cards',
    orders: 'orders',
    bonus: 'bonus',
  } as const;

  const PROFILE_PAGE_VALUES = Object.values(PROFILE_PAGES);

  const isProfileSectionPage = (
    page: string | null,
  ): page is typeof PROFILE_PAGE_VALUES[number] => {
    if (!page) {
      return false;
    }

    return PROFILE_PAGE_VALUES.includes(
      page as typeof PROFILE_PAGE_VALUES[number],
    );
  };

  const currentPage = searchParams.get('page');

  const isInnerProfilePage = isProfileSectionPage(currentPage);
  const isMainProfilePage = !isInnerProfilePage;



  const goBack = () => {
    if (isInnerProfilePage) {
      navigate('/profile');
      return;
    }

    const isDefault =
      ((window.history.state && window.history.state.key) || 'default') ===
      'default';

    if (isDefault) {
      navigate('/', { replace: true });
      return;
    }

    navigate(-1);
  };

  const handleLogOutClick = () => {
    handleLogOut();
    navigate('/');
    window.scrollTo({ top: 0 });
  };

  const renderProfilePage = () => {
    if (!isProfileSectionPage(currentPage)) {
      return null;
    }

    switch (currentPage) {
      case PROFILE_PAGES.editProfile:
        return <EditProfilePage open />;

      case PROFILE_PAGES.addresses:
        return <SavedAddresses />;

      case PROFILE_PAGES.cards:
        return <SavedCards />;

      case PROFILE_PAGES.orders:
        return <OrdersPage open />;

      case PROFILE_PAGES.bonus:
        return <BonusPage open />;

      default:
        return null;
    }
  };

  if (!isAuth) {
    return <Navigate to="/?page=auth" replace />;
  }

  return (
    <div className={style.profilePage}>
      {isMainProfilePage && (
        <>
          <div className={style.pageHeader}>
            <button
              onClick={() => navigate('/')}
              className={clsx('button-arrowBack', style.backButton)}
            />

            <h2 className={is500 ? 'title' : 'headline'}>
              {t('Профиль')}
            </h2>
          </div>

          <div className={style.contentGrid}>
            <CustomerCard />

            <div className={style.secondaryGrid}>
              <SavedCardsCard
                onChangeWithCards={setWithCards}
                className={clsx(
                  style.pageItemContainer,
                  style.savedCards,
                )}
              />

              <div
                with-cards={withCards.toString()}
                onClick={() => navigate(SAVED_ADDRESSES_PATHNAME)}
                className={clsx(
                  style.pageItemContainer,
                  style.pageItemContainerDisplay,
                  style.addresses,
                )}
              >
                <p className={is500 ? 'title' : 'headline'}>
                  {t('profile.addressesTitle')}
                </p>
                <NavigateButton
                  className={style.navigateButton}
                  onClick={() => navigate(SAVED_ADDRESSES_PATHNAME)}
                />
              </div>

              <div
                with-cards={withCards.toString()}
                onClick={() => navigate('/profile?page=orders')}
                className={clsx(
                  style.pageItemContainer,
                  style.pageItemContainerDisplay,
                  style.orders,
                )}
              >
                <p className={is500 ? 'title' : 'headline'}>
                  {t('profile.myOrders')}
                </p>

                <NavigateButton
                  className={style.navigateButton}
                  onClick={() => navigate('/profile?page=orders')}
                />
              </div>

              <div
                with-cards={withCards.toString()}
                onClick={() => navigate('/profile?page=bonus')}
                className={clsx(
                  style.pageItemContainer,
                  style.pageItemContainerDisplay,
                  style.bonusHistory,
                )}
              >
                <p className={is500 ? 'title' : 'headline'}>
                  {t('profile.bonusHistory')}
                </p>

                <NavigateButton
                  className={style.navigateButton}
                  onClick={() => navigate('/profile?page=bonus')}
                />
              </div>
            </div>

            {!is1024 && (
              <div className={style.logOutButtonContainer}>
                <FEButton
                  onClick={handleLogOutClick}
                  type="secondary"
                >
                  {t('Выйти')}
                </FEButton>
              </div>
            )}
          </div>
        </>
      )}

      { isInnerProfilePage && (
        <ProfileHeaderProvider>
          <ProfileSection
            currentProfilePage={currentPage}
            is1024={is1024}
            is500={is500}
            defaultTitle={t(getProfilePageTitle(currentPage))}
            onDefaultBack={goBack}
          >
            {renderProfilePage()}
          </ProfileSection>
        </ProfileHeaderProvider>
      )}
    </div>
  );
};

const getProfilePageTitle = (page: string | null) => {
  switch (page) {
    case PROFILE_PAGES.editProfile:
      return 'Мои данные';

    case PROFILE_PAGES.addresses:
      return 'Мои адреса';

    case PROFILE_PAGES.cards:
      return 'Банковские карты';

    case PROFILE_PAGES.orders:
      return 'Мои заказы';

    case PROFILE_PAGES.bonusHistory:
      return 'История икринок';

    default:
      return 'Профиль';
  }
};
